/**
 * BatchCreationDialog.jsx
 * ======================
 * 
 * A 3-step modal dialog for creating batches when marking orders as "Ready" status.
 * Part of the Order Pipeline feature in the Weekly Planner.
 * 
 * ## Workflow Steps:
 * 1. **Step 1: Batch Entry** - Enter batch codes (min 6 chars), quantities, weights, and cases
 * 2. **Step 2: Use-By Dates** - Assign a use-by date to each product's batches
 * 3. **Step 3: Review** - Review all batch details before final creation
 * 
 * ## Features:
 * - Multi-batch support per product (split quantities across batches)
 * - Existing batch selection dropdown
 * - Real-time quantity validation with percentage indicators
 * - Soft warnings for quantity mismatches (doesn't block submission)
 * - Supplier code and co-branding display for customer requirements
 * 
 * ## Props:
 * @param {boolean} open - Dialog visibility state
 * @param {function} onOpenChange - Handler for dialog state changes
 * @param {object} pendingReadyDispatch - The dispatch being processed
 * @param {array} products - All available products
 * @param {array} batches - All available batches for selection
 * @param {array} cobrandings - Co-branding options for display
 * @param {number} batchStep - Current step (1, 2, or 3)
 * @param {function} setBatchStep - Step state setter
 * @param {object} productBatches - Batch data keyed by product index
 * @param {function} setProductBatches - Batch data setter
 * @param {object} productUseDates - Use-by dates keyed by product index
 * @param {function} setProductUseDates - Use-by date setter
 * @param {array} batchLines - Final generated batch lines for creation
 * @param {function} setBatchLines - Batch lines setter
 * @param {function} getTotalBatchQty - Calculate total quantity for a product
 * @param {function} validateBatchCodes - Validate all batch codes are valid
 * @param {function} validateBatchValues - Validate quantities with warnings
 * @param {function} validateUseDates - Validate all use-by dates are set
 * @param {function} generateFinalBatchLines - Generate final batch data
 * @param {function} handleReadyWithBatch - Submit handler to create batches
 * @param {boolean} creatingBatch - Loading state during creation
 * 
 * @author Cashel Blue Operations Hub Team
 * @lastModified February 21, 2025
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { DatePicker } from '../../components/ui/date-picker';
import { 
  Loader2, 
  Boxes,
  AlertTriangle,
  Trash2,
  Plus,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
  Lightbulb,
  BookOpen,
  Lock,
  Unlock,
  Save,
  Sparkles,
  Split
} from 'lucide-react';
import { lookupFormula, learnBatchFormula, predictUseByDates, getPoGroup } from '../../lib/api';
import { PoTallyPanel } from '../../components/phased/PoTallyPanel';
import { buildPoTally, lineKey, nextOpenPart } from '../../components/phased/phasedUtils';

/**
 * Main BatchCreationDialog Component
 */
export default function BatchCreationDialog({
  open,
  onOpenChange,
  pendingReadyDispatch,
  products,
  batches,
  cobrandings = [], // Added for supplier code display
  // Batch state
  batchStep,
  setBatchStep,
  productBatches,
  setProductBatches,
  productUseDates,
  setProductUseDates,
  productNotes,
  setProductNotes,
  outOfStockProducts,
  setOutOfStockProducts,
  batchLines,
  setBatchLines,
  // Validation functions
  getTotalBatchQty,
  validateBatchCodes,
  validateBatchValues,
  validateUseDates,
  generateFinalBatchLines,
  // Handlers
  handleReadyWithBatch,
  creatingBatch,
  // Save-as-Draft (persists partial productBatches/productUseDates without locking)
  handleSaveDraft,
  savingDraft = false,
  // Per-customer feature flags
  showDom = false,  // When true, surface a "D.O.M." (Date of Manufacture) input on each batch row.
                    // Driven by the customer's `print_dom_on_dispatch` flag in WeeklyPlanner.
  customerAutoCalcWeight = false,  // When true, auto-fill weight_kg = quantity × product.weight_per_unit on units entry.
  editingBatchData = false,  // When true, this dialog is editing an already-Ready order — adjust title/button copy and skip "lock" warnings.
  palletCount = '',          // Actual pallet count (overrides the SO estimate). Persisted to dispatch.num_pallets on Confirm.
  setPalletCount = () => {},
  onSplitFulfilment,         // Seasonal orders only: send what's ready now, keep the rest on a linked order
}) {
  // Warning confirmation state
  const [batchValidationWarnings, setBatchValidationWarnings] = useState([]);
  const [showWarningConfirm, setShowWarningConfirm] = useState(false);
  // No Use By Date tracking per product index
  const [noUseDateProducts, setNoUseDateProducts] = useState({});
  // Track newly added batch for auto-focus
  const [focusTarget, setFocusTarget] = useState(null);
  const focusRef = useRef(null);
  // Batch formula per product line index: { [productIndex]: { batch_units_per_case, source } }
  const [batchFormulas, setBatchFormulas] = useState({});
  // Use-By auto-suggest per product line index (learned per Customer Product Code)
  const [useByPredictions, setUseByPredictions] = useState({});
  // Phased PO: whole-PO tally (all linked parts) for the PO pills
  const [poSummary, setPoSummary] = useState(null);
  const poGroupId = pendingReadyDispatch?.po_group_id;
  useEffect(() => {
    if (!open || !poGroupId) { setPoSummary(null); return; }
    let cancelled = false;
    getPoGroup(poGroupId).then(r => { if (!cancelled) setPoSummary(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [open, poGroupId]);

  const predictionTitle = (p) => {
    if (!p) return '';
    if (p.source === 'code_history') return `Auto-suggested: learned from ${p.sample_count} past order${p.sample_count === 1 ? '' : 's'} for code ${p.supplier_product_code} (${p.shelf_life_days} days shelf life). Fully editable.`;
    if (p.source === 'manual') return `Auto-suggested: manually set shelf life for code ${p.supplier_product_code} (${p.shelf_life_days} days). Fully editable.`;
    return `Auto-suggested from product default shelf life (${p.shelf_life_days} days). Fully editable.`;
  };

  // Prevent mouse wheel from changing number inputs
  const preventScroll = (e) => e.target.blur();

  // Auto-focus on new batch line when added
  useEffect(() => {
    if (focusTarget && focusRef.current) {
      focusRef.current.focus();
      setFocusTarget(null);
    }
  }, [focusTarget, productBatches]);

  // Fetch batch formulas for all product lines when dialog opens
  useEffect(() => {
    if (!open || !pendingReadyDispatch) return;
    const lines = pendingReadyDispatch.lines || [];
    const customerId = pendingReadyDispatch.customer_id;
    if (!customerId || lines.length === 0) return;
    
    const fetchFormulas = async () => {
      const formulas = {};
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!line.product_id) continue;
        try {
          const { data } = await lookupFormula(customerId, line.product_id, line.cobranding_id || '');
          if (data.batch_units_per_case || data.unit_weight_kg) {
            formulas[i] = {
              batch_units_per_case: data.batch_units_per_case || null,
              units_per_case: data.units_per_case || null,
              unit_weight_kg: data.unit_weight_kg || null,
              source: data.batch_formula_source || data.formula_source || 'learned',
            };
          }
        } catch { /* ignore */ }
      }
      setBatchFormulas(formulas);
    };
    fetchFormulas();
  }, [open, pendingReadyDispatch]);

  // Fetch Use-By predictions (per Customer Product Code) when dialog opens.
  // Auto-fills any empty Use-By date with the predicted date — fully editable.
  useEffect(() => {
    if (!open || !pendingReadyDispatch) return;
    const lines = pendingReadyDispatch.lines || [];
    const customerId = pendingReadyDispatch.customer_id;
    if (!customerId || lines.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await predictUseByDates({
          customer_id: customerId,
          dispatch_date: pendingReadyDispatch.dispatch_date || '',
          lines: lines.map(l => ({ supplier_product_code: l.supplier_product_code || null, product_id: l.product_id || null })),
        });
        if (cancelled) return;
        const preds = {};
        (data.predictions || []).forEach((p, i) => {
          if (p.found && p.predicted_date) preds[i] = p;
        });
        setUseByPredictions(preds);
        setProductUseDates(prev => {
          const next = { ...prev };
          let changed = false;
          Object.entries(preds).forEach(([i, p]) => {
            if (!next[i]) { next[i] = p.predicted_date; changed = true; }
          });
          return changed ? next : prev;
        });
      } catch { /* non-critical */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingReadyDispatch]);

  // Add a batch to a product
  const addBatchToProduct = (productIndex) => {
    setProductBatches(prev => {
      const newBatches = [...(prev[productIndex] || []), { batch_code: '', quantity: '', weight_kg: '', cases: '', dom_date: '' }];
      return { ...prev, [productIndex]: newBatches };
    });
    setFocusTarget({ productIndex, batchIndex: (productBatches[productIndex] || []).length });
  };

  // Remove a batch from a product
  const removeBatchFromProduct = (productIndex, batchIndex) => {
    setProductBatches(prev => {
      const currentBatches = [...(prev[productIndex] || [])];
      if (currentBatches.length > 1) {
        currentBatches.splice(batchIndex, 1);
      }
      return { ...prev, [productIndex]: currentBatches };
    });
  };

  // Conversion factors for a product line: units-per-case + kg-per-unit.
  // Priority: customer formula (batch / sales-order, learned or manual) →
  // product fixed-weight metadata (or customer auto-weight flag).
  const getConversion = (productIndex) => {
    const f = batchFormulas[productIndex];
    const product = products.find(p => p.id === pendingReadyDispatch?.lines?.[productIndex]?.product_id);
    const productFallback = customerAutoCalcWeight || !!product?.is_fixed_weight;
    const upc = parseFloat(f?.batch_units_per_case) || parseFloat(f?.units_per_case)
      || (productFallback ? parseFloat(product?.units_per_case) || 0 : 0);
    const wpu = parseFloat(f?.unit_weight_kg)
      || (productFallback ? parseFloat(product?.weight_per_unit) || 0 : 0);
    return { upc, wpu };
  };

  const LOCK_KEY = { quantity: 'units', cases: 'cases', weight_kg: 'weight' };
  const isAutoField = (productIndex, batch, field) => {
    if (batch.manual_override || !batch.lockedField || batch.lockedField === LOCK_KEY[field]) return false;
    if (batch[field] === '' || batch[field] === undefined || batch[field] === null) return false;
    const { upc, wpu } = getConversion(productIndex);
    if (field === 'weight_kg') return wpu > 0;
    if (field === 'cases') return upc > 0;
    return batch.lockedField === 'cases' ? upc > 0 : wpu > 0;
  };

  const r1 = (n) => (Math.round(n * 10) / 10).toString();
  const r2 = (n) => (Math.round(n * 100) / 100).toString();

  // Units pre-filled from the order get cases/weight from the formula, same as typing them
  useEffect(() => {
    if (!open) return;
    setProductBatches(prev => {
      let changed = false;
      const next = { ...prev };
      Object.entries(prev || {}).forEach(([idx, batches]) => {
        const { upc, wpu } = getConversion(Number(idx));
        if (!(upc > 0 || wpu > 0)) return;
        next[idx] = (batches || []).map(b => {
          const q = parseFloat(b.quantity);
          const empty = (v) => v === '' || v === undefined || v === null;
          if (!(q > 0) || b.manual_override || b.lockedField || !empty(b.cases) || !empty(b.weight_kg)) return b;
          changed = true;
          return { ...b, lockedField: 'units', ...(upc > 0 && { cases: r1(q / upc) }), ...(wpu > 0 && { weight_kg: r2(q * wpu) }) };
        });
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, batchFormulas, products, customerAutoCalcWeight]);

  // Whole-PO tally for a line: every line of the same product on this part counts as "this delivery"
  const getPoTally = (productIndex) => {
    if (!poSummary) return null;
    const lines = pendingReadyDispatch?.lines || [];
    const key = lineKey(lines[productIndex] || {});
    const row = poSummary.lines.find(r => lineKey(r) === key);
    if (!row) return null;
    const entered = [0, 0, 0];
    lines.forEach((l, i) => {
      if (lineKey(l) !== key || outOfStockProducts[i]) return;
      (productBatches[i] || []).forEach(b => {
        entered[0] += parseFloat(b.quantity) || 0;
        entered[1] += parseFloat(b.cases) || 0;
        entered[2] += parseFloat(b.weight_kg) || 0;
      });
    });
    const self = poSummary.parts.find(p => p.id === pendingReadyDispatch.id);
    return buildPoTally({
      row, entered, conv: getConversion(productIndex), selfState: self?.state,
      thisLines: lines.filter(l => lineKey(l) === key),
    });
  };

  // Update a specific batch for a product with three-way auto-calculation.
  //
  // Behaviour:
  // 1. Typing into ANY of Units / Cases / Weight derives the other two from the
  //    conversion factors (see getConversion). Units ↔ Cases needs units-per-case;
  //    Weight needs kg-per-unit.
  // 2. The field the user just typed into becomes the "lockedField" — the others
  //    show an "auto" badge.
  // 3. Clearing the locked field (empty string) drops the lock and clears what was
  //    derived from it so the user can start fresh in any direction.
  // 4. `manual_override` flag disables ALL auto-calc for that batch row — fields
  //    behave as plain inputs (toggle via the lock icon in the row).
  const updateProductBatch = (productIndex, batchIndex, field, value) => {
    setProductBatches(prev => {
      const currentBatches = [...(prev[productIndex] || [])];
      const batch = { ...currentBatches[batchIndex], [field]: value };
      const { upc, wpu } = getConversion(productIndex);
      const isDriver = field === 'quantity' || field === 'cases' || field === 'weight_kg';

      if (isDriver && !batch.manual_override && (upc > 0 || wpu > 0)) {
        const num = parseFloat(value);
        if (value === '' || isNaN(num)) {
          batch.lockedField = null;
          if (field === 'quantity') {
            if (upc > 0) batch.cases = '';
            if (wpu > 0) batch.weight_kg = '';
          } else if (field === 'cases' && upc > 0) {
            batch.quantity = '';
            if (wpu > 0) batch.weight_kg = '';
          } else if (field === 'weight_kg' && wpu > 0) {
            batch.quantity = '';
            if (upc > 0) batch.cases = '';
          }
        } else if (num > 0) {
          if (field === 'quantity') {
            batch.lockedField = 'units';
            // 1 decimal place — Traditional Cheese Lidl stock may have a partial case
            if (upc > 0) batch.cases = r1(num / upc);
            if (wpu > 0) batch.weight_kg = r2(num * wpu);
          } else if (field === 'cases' && upc > 0) {
            batch.lockedField = 'cases';
            const qty = Math.round(num * upc);
            batch.quantity = qty.toString();
            if (wpu > 0) batch.weight_kg = r2(qty * wpu);
          } else if (field === 'weight_kg' && wpu > 0) {
            batch.lockedField = 'weight';
            const qty = Math.round(num / wpu);
            batch.quantity = qty > 0 ? qty.toString() : '';
            if (upc > 0) batch.cases = qty > 0 ? r1(qty / upc) : '';
          }
        }
      }

      currentBatches[batchIndex] = batch;
      return { ...prev, [productIndex]: currentBatches };
    });
  };

  // Handle step 1 continue with validation (now goes directly to review)
  const handleStep1Continue = () => {
    // Validate use-by dates are filled
    if (!validateUseDates()) {
      return; // validateUseDates should show errors
    }
    const validation = validateBatchValues();
    if (validation.warnings.length > 0) {
      setBatchValidationWarnings(validation.warnings);
      setShowWarningConfirm(true);
    } else {
      generateFinalBatchLines();
    }
  };

  // Handle dialog close
  const handleClose = (isOpen) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      setBatchStep(1);
      setProductBatches({});
      setProductUseDates({});
      setBatchValidationWarnings([]);
      setBatchFormulas({});
      setUseByPredictions({});
      setShowWarningConfirm(false);
      setNoUseDateProducts({});
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-4xl max-h-[85vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Close button is in DialogContent by default via the X */}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Boxes className="w-5 h-5" />
            {editingBatchData
              ? (batchStep === 1 ? 'Edit Batch Data — Step 1: Codes, Quantities & Use-By' : 'Edit Batch Data — Step 2: Review & Update')
              : (batchStep === 1 ? 'Step 1: Batch Codes, Quantities & Use-By Dates' : 'Step 2: Review & Create')}
          </DialogTitle>
          <DialogDescription>
            {editingBatchData && (
              <span className="block text-amber-700 font-medium mb-1">
                Editing existing batch data — order status will NOT change. Existing batches with the same code will be reused.
              </span>
            )}
            {batchStep === 1 && 'Enter batch codes (min 6 chars), units, weight, and use-by date for each product.'}
            {batchStep === 3 && 'Review batch details before creating dispatch note.'}
          </DialogDescription>
        </DialogHeader>
        
        {pendingReadyDispatch && (() => {
          // Calculate actual batch totals from entered data
          const allBatchEntries = Object.values(productBatches).flat();
          const actualUnits = allBatchEntries.reduce((sum, b) => sum + (parseFloat(b.quantity) || 0), 0);
          const actualCases = allBatchEntries.reduce((sum, b) => sum + (parseFloat(b.cases) || 0), 0);
          const actualWeight = allBatchEntries.reduce((sum, b) => sum + (parseFloat(b.weight_kg) || 0), 0);
          const hasBatchData = allBatchEntries.length > 0 && (actualUnits > 0 || actualWeight > 0);
          const showBatchTotals = batchStep >= 2 && hasBatchData;
          
          return (
            <div className="bg-muted/30 rounded-lg p-3 mb-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm">
                    <span className="font-medium">{pendingReadyDispatch.customer_name}</span>
                    {pendingReadyDispatch.po_number && <span className="text-muted-foreground"> • PO: {pendingReadyDispatch.po_number}</span>}
                  </p>
                </div>
                {/* Summary - Top RHS: Show batch actuals on step 2+, order data on step 1 */}
                <div className="flex gap-4 text-sm">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">{showBatchTotals ? 'Batch Units' : 'Order Units'}</p>
                    <p className="font-mono font-semibold text-base">
                      {showBatchTotals
                        ? actualUnits.toFixed(0)
                        : (pendingReadyDispatch?.lines || []).reduce((sum, l) => sum + (parseFloat(l.quantity) || 0), 0).toFixed(0)
                      }
                    </p>
                  </div>
                  <div className="text-right border-l pl-4">
                    <p className="text-xs text-muted-foreground">{showBatchTotals ? 'Batch Cases' : 'Order Cases'}</p>
                    <p className="font-mono font-semibold text-base text-purple-600">
                      {showBatchTotals
                        ? actualCases.toFixed(1)
                        : (pendingReadyDispatch?.lines || []).reduce((sum, l) => {
                            const lineCases = parseFloat(l.cases || l.calculated_cases) || 0;
                            if (lineCases > 0) return sum + lineCases;
                            const product = products.find(p => p.id === l.product_id);
                            const unitsPerCase = product?.units_per_case || 12;
                            return sum + ((parseFloat(l.quantity) || 0) / unitsPerCase);
                          }, 0).toFixed(1)
                      }
                    </p>
                  </div>
                  <div className="text-right border-l pl-4">
                    <p className="text-xs text-muted-foreground">{showBatchTotals ? 'Batch Weight (kg)' : 'Order Weight (kg)'}</p>
                    <p className="font-mono font-semibold text-base text-blue-600">
                      {showBatchTotals
                        ? actualWeight.toFixed(2)
                        : (pendingReadyDispatch?.lines || []).reduce((sum, l) => {
                            const lineWeight = parseFloat(l.estimated_weight || l.weight_override || l.total_weight) || 0;
                            if (lineWeight > 0) return sum + lineWeight;
                            const product = products.find(p => p.id === l.product_id);
                            const weightPerUnit = product?.weight_per_unit || 0;
                            return sum + ((parseFloat(l.quantity) || 0) * weightPerUnit);
                          }, 0).toFixed(2)
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
        
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 py-2">
          {[1, 2].map(step => {
            const isActive = (step === 1 && batchStep === 1) || (step === 2 && batchStep === 3);
            const isPast = (step === 1 && batchStep === 3);
            return (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  isActive || isPast ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}>
                  {step}
                </div>
                {step < 2 && <div className={`w-12 h-1 mx-1 ${isPast ? 'bg-primary' : 'bg-muted'}`} />}
              </div>
            );
          })}
        </div>
        
        {/* Step 1: Batch Codes & Quantities */}
        {batchStep === 1 && (
          <div className="space-y-4 py-4">
            {(pendingReadyDispatch?.lines || []).map((line, productIndex) => {
              const product = products.find(p => p.id === line.product_id);
              const isProductMissing = !product && line.product_id;
              const currentBatches = productBatches[productIndex] || [];
              const totalQty = getTotalBatchQty(productIndex);
              const orderedQty = parseFloat(line.quantity) || 0;
              const orderedWeightFromLine = parseFloat(line.estimated_weight || line.weight_override || line.total_weight) || 0;
              const orderedWeight = orderedWeightFromLine > 0 ? orderedWeightFromLine : (orderedQty * (product?.weight_per_unit || 0));
              const orderedCasesFromLine = parseFloat(line.cases || line.calculated_cases) || 0;
              const orderedCases = orderedCasesFromLine > 0 ? orderedCasesFromLine : (orderedQty / (product?.units_per_case || 12));
              const totalWeight = currentBatches.reduce((sum, b) => sum + (parseFloat(b.weight_kg) || 0), 0);
              const conv = getConversion(productIndex);
              const hasAutoFill = conv.upc > 0 || conv.wpu > 0;
              const needsLearning = !batchFormulas[productIndex]?.batch_units_per_case || !batchFormulas[productIndex]?.unit_weight_kg;
              
              return (
                <div key={productIndex} className={`border rounded-lg p-4 ${outOfStockProducts[productIndex] ? 'bg-red-50/50 border-red-200' : isProductMissing ? 'bg-red-50 border-red-300' : 'bg-white'}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">
                        {line.product_name || product?.name || 'Product'}
                        {/* Product status warning */}
                        {isProductMissing && (
                          <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded">NOT FOUND</span>
                        )}
                        {outOfStockProducts[productIndex] && (
                          <span className="ml-2 px-2.5 py-0.5 bg-red-600 text-white text-xs font-bold rounded tracking-wide">SEASONALLY OUT OF STOCK</span>
                        )}
                        {/* Show Customer Code and Brand/Packaging in red */}
                        {(line.supplier_product_code || line.cobranding_id) && (
                          <span className="ml-2 text-red-600 font-normal text-sm">
                            {line.supplier_product_code && (
                              <span className="mr-2">[{line.supplier_product_code}]</span>
                            )}
                            {line.cobranding_id && cobrandings.find(c => c.id === line.cobranding_id) && (
                              <span>({cobrandings.find(c => c.id === line.cobranding_id)?.name})</span>
                            )}
                          </span>
                        )}
                      </p>
                      {isProductMissing && (
                        <p className="text-red-600 text-sm mt-1">
                          This product was not found. It may have been deleted.
                        </p>
                      )}
                      <div className="flex flex-wrap gap-x-4 text-sm text-muted-foreground">
                        <span className="whitespace-nowrap">Ordered: <span className="font-mono font-medium">{orderedQty}</span> units</span>
                        {(line.cases || line.calculated_cases) > 0 && (
                          <span className="text-purple-600 font-medium whitespace-nowrap">| Cases: <span className="font-mono">{parseFloat(line.cases || line.calculated_cases || 0).toFixed(1)}</span></span>
                        )}
                        {orderedWeight > 0 && (
                          <span className="text-blue-600 font-medium whitespace-nowrap">| Weight: <span className="font-mono">{orderedWeight.toFixed(2)}</span> kg</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        <span>Variations will show as warnings</span>
                      </div>
                      {line.notes && (
                        <div className="mt-1.5 px-2.5 py-1.5 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                          <span className="font-medium text-xs text-blue-600 mr-1">Note:</span>{line.notes}
                        </div>
                      )}
                      {/* Conversion indicator — auto-fill works from any of Units / Cases / Weight */}
                      {hasAutoFill && (
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-600" data-testid={`batch-autofill-hint-${productIndex}`}>
                          <Lightbulb className="w-3 h-3" />
                          <span>
                            Auto-fill from any field:
                            {conv.upc > 0 && ` 1 case = ${conv.upc} units`}
                            {conv.upc > 0 && conv.wpu > 0 && ' ·'}
                            {conv.wpu > 0 && ` 1 unit = ${Number(conv.wpu.toFixed(4))} kg`}
                          </span>
                        </div>
                      )}
                      {needsLearning && !outOfStockProducts[productIndex] && (
                        <div className="mt-1 flex items-center gap-1.5 text-xs text-blue-400" data-testid={`batch-learn-hint-${productIndex}`}>
                          <BookOpen className="w-3 h-3" />
                          <span>System will learn the {!batchFormulas[productIndex]?.batch_units_per_case && !batchFormulas[productIndex]?.unit_weight_kg ? 'case and weight' : !batchFormulas[productIndex]?.unit_weight_kg ? 'weight' : 'case'} formula from this entry</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {outOfStockProducts[productIndex] ? (
                        <div className="text-sm font-bold px-4 py-2 rounded bg-red-100 text-red-700">
                          Line will be excluded from totals
                        </div>
                      ) : (<>
                      {(() => {
                        const qtyPercent = orderedQty > 0 ? ((totalQty - orderedQty) / orderedQty) * 100 : 0;
                        const hasVariation = Math.abs(qtyPercent) > 0.1;
                        const qtyClass = hasVariation ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
                        const displayQty = Number.isInteger(totalQty) ? totalQty : totalQty.toFixed(2);
                        const displayOrdered = Number.isInteger(orderedQty) ? orderedQty : orderedQty.toFixed(2);
                        return (
                          <div className={`text-sm font-medium px-3 py-1 rounded ${qtyClass}`}>
                            Units: {displayQty} / {displayOrdered} {hasVariation && `(${qtyPercent > 0 ? '+' : ''}${qtyPercent.toFixed(0)}%)`}
                          </div>
                        );
                      })()}
                      {orderedWeight > 0 && (() => {
                        const weightPercent = orderedWeight > 0 ? ((totalWeight - orderedWeight) / orderedWeight) * 100 : 0;
                        const hasVariation = Math.abs(weightPercent) > 0.1;
                        const weightClass = hasVariation ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
                        return (
                          <div className={`text-sm font-medium px-3 py-1 rounded ${weightClass}`}>
                            Wt: {totalWeight.toFixed(2)} / {orderedWeight.toFixed(2)} kg {hasVariation && `(${weightPercent > 0 ? '+' : ''}${weightPercent.toFixed(0)}%)`}
                          </div>
                        );
                      })()}
                      {orderedCases > 0 && (() => {
                        const totalCases = currentBatches.reduce((sum, b) => sum + (parseFloat(b.cases) || 0), 0);
                        const casesPercent = orderedCases > 0 ? ((totalCases - orderedCases) / orderedCases) * 100 : 0;
                        const hasVariation = Math.abs(casesPercent) > 0.1;
                        const casesClass = hasVariation ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700';
                        return (
                          <div className={`text-sm font-medium px-3 py-1 rounded ${casesClass}`}>
                            Cases: {totalCases.toFixed(1)} / {orderedCases.toFixed(1)} {hasVariation && `(${casesPercent > 0 ? '+' : ''}${casesPercent.toFixed(0)}%)`}
                          </div>
                        );
                      })()}
                      {(() => {
                        const tally = getPoTally(productIndex);
                        return tally && (
                          <PoTallyPanel
                            tally={tally}
                            index={productIndex}
                            label={pendingReadyDispatch?.fulfilment_label}
                            next={nextOpenPart(poSummary, pendingReadyDispatch?.id)}
                          />
                        );
                      })()}
                      </>)}
                    </div>
                  </div>
                  
                  {outOfStockProducts[productIndex] ? (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md text-center">
                      <p className="text-sm text-red-700 font-medium">This product will appear on the dispatch note as Seasonally Out Of Stock</p>
                    </div>
                  ) : (
                  <div className="space-y-2">
                    {currentBatches.map((batch, batchIndex) => (
                      <div key={batchIndex} className="space-y-1">
                        <div className="flex items-center gap-3 p-2 bg-muted/30 rounded">
                          <div className="flex-1">
                            <Label className="text-xs">Batch Code (6-8 chars) *</Label>
                            <Input
                              ref={focusTarget?.productIndex === productIndex && focusTarget?.batchIndex === batchIndex ? focusRef : undefined}
                              value={batch.batch_code}
                              onChange={(e) => updateProductBatch(productIndex, batchIndex, 'batch_code', e.target.value.slice(0, 8))}
                              maxLength={8}
                              placeholder="6-8 chars"
                              className={`font-mono text-sm h-9 ${batch.batch_code.length >= 6 && batch.batch_code.length <= 8 ? 'border-green-500' : batch.batch_code.length > 0 ? 'border-red-500' : ''}`}
                              data-testid={`batch-code-${productIndex}-${batchIndex}`}
                            />
                            {batch.batch_code.length > 0 && batch.batch_code.length < 6 && (
                              <span className="text-xs text-red-500">{6 - batch.batch_code.length} more chars needed</span>
                            )}
                          </div>
                          <div className="w-28">
                            <Label className="text-xs text-blue-600 font-medium">
                              Weight (kg) *
                              {isAutoField(productIndex, batch, 'weight_kg') && (
                                <span className="ml-1 text-[9px] text-emerald-600 font-medium">auto</span>
                              )}
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={batch.weight_kg}
                              onChange={(e) => updateProductBatch(productIndex, batchIndex, 'weight_kg', e.target.value)}
                              onWheel={preventScroll}
                              placeholder="0.00"
                              className={`font-mono text-sm h-9 border-blue-300 focus:border-blue-500 ${isAutoField(productIndex, batch, 'weight_kg') ? 'bg-emerald-50 border-emerald-300' : ''}`}
                              data-testid={`batch-weight-${productIndex}-${batchIndex}`}
                            />
                          </div>
                          <div className="w-24">
                            <Label className="text-xs">
                              Cases
                              {isAutoField(productIndex, batch, 'cases') && (
                                <span className="ml-1 text-[9px] text-emerald-600 font-medium">auto</span>
                              )}
                            </Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={batch.cases || ''}
                              onChange={(e) => updateProductBatch(productIndex, batchIndex, 'cases', e.target.value)}
                              onWheel={preventScroll}
                              placeholder="0.0"
                              className={`font-mono text-sm h-9 ${isAutoField(productIndex, batch, 'cases') ? 'bg-emerald-50 border-emerald-300' : ''}`}
                              data-testid={`batch-cases-${productIndex}-${batchIndex}`}
                            />
                          </div>
                          <div className="w-24">
                            <Label className="text-xs">
                              Units *
                              {isAutoField(productIndex, batch, 'quantity') && (
                                <span className="ml-1 text-[9px] text-emerald-600 font-medium">auto</span>
                              )}
                            </Label>
                            <Input
                              type="number"
                              value={batch.quantity}
                              onChange={(e) => updateProductBatch(productIndex, batchIndex, 'quantity', e.target.value)}
                              onWheel={preventScroll}
                              placeholder="Qty"
                              className={`font-mono text-sm h-9 ${isAutoField(productIndex, batch, 'quantity') ? 'bg-emerald-50 border-emerald-300' : ''}`}
                              data-testid={`batch-qty-${productIndex}-${batchIndex}`}
                            />
                          </div>
                          {showDom && (
                            <div className="w-36">
                              <Label className="text-xs text-purple-700 font-medium">D.O.M.</Label>
                              <DatePicker
                                value={batch.dom_date || ''}
                                onChange={(d) => updateProductBatch(productIndex, batchIndex, 'dom_date', d)}
                                placeholder="Date of mfg"
                                data-testid={`batch-dom-${productIndex}-${batchIndex}`}
                              />
                            </div>
                          )}
                          {hasAutoFill && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-9 w-9 p-0 mt-5 ${batch.manual_override ? 'text-amber-600 hover:text-amber-700' : 'text-muted-foreground hover:text-foreground'}`}
                              onClick={() => updateProductBatch(productIndex, batchIndex, 'manual_override', !batch.manual_override)}
                              title={batch.manual_override ? 'Manual entry ON — auto-calc disabled. Click to re-enable.' : 'Auto-calc ON. Click to switch to manual entry.'}
                              data-testid={`batch-manual-toggle-${productIndex}-${batchIndex}`}
                            >
                              {batch.manual_override ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                            </Button>
                          )}
                          {currentBatches.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-9 w-9 p-0 text-red-500 hover:text-red-700 mt-5"
                              onClick={() => removeBatchFromProduct(productIndex, batchIndex)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        {/* Per-batch use-by date override (only when multiple batches) */}
                        {currentBatches.length > 1 && (
                          <div className="flex items-center gap-3 pl-2 pb-1">
                            <Checkbox
                              id={`diff-date-${productIndex}-${batchIndex}`}
                              checked={batch.has_own_date || false}
                              onCheckedChange={(checked) => {
                                updateProductBatch(productIndex, batchIndex, 'has_own_date', checked);
                                if (!checked) {
                                  updateProductBatch(productIndex, batchIndex, 'own_use_by_date', '');
                                  updateProductBatch(productIndex, batchIndex, 'own_no_date', false);
                                }
                              }}
                              className="h-3.5 w-3.5"
                              data-testid={`diff-date-${productIndex}-${batchIndex}`}
                            />
                            <label htmlFor={`diff-date-${productIndex}-${batchIndex}`} className="text-[11px] text-muted-foreground cursor-pointer">
                              Different use-by date for this batch
                            </label>
                            {batch.has_own_date && (
                              <div className="flex items-center gap-2 ml-2">
                                {batch.own_no_date ? (
                                  <div className="h-8 flex items-center px-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700 font-medium">No Date</div>
                                ) : (
                                  <div className="w-40">
                                    <DatePicker
                                      value={batch.own_use_by_date || ''}
                                      onChange={(d) => updateProductBatch(productIndex, batchIndex, 'own_use_by_date', d)}
                                      placeholder="Select date"
                                      defaultMonth={useByPredictions[productIndex]?.predicted_date}
                                      data-testid={`batch-own-date-${productIndex}-${batchIndex}`}
                                    />
                                  </div>
                                )}
                                <Checkbox
                                  id={`batch-no-date-${productIndex}-${batchIndex}`}
                                  checked={batch.own_no_date || false}
                                  onCheckedChange={(checked) => {
                                    updateProductBatch(productIndex, batchIndex, 'own_no_date', checked);
                                    if (checked) updateProductBatch(productIndex, batchIndex, 'own_use_by_date', 'No Date');
                                    else updateProductBatch(productIndex, batchIndex, 'own_use_by_date', '');
                                  }}
                                  className="h-3 w-3"
                                  data-testid={`batch-own-nodate-${productIndex}-${batchIndex}`}
                                />
                                <label htmlFor={`batch-no-date-${productIndex}-${batchIndex}`} className="text-[10px] text-muted-foreground cursor-pointer">No date</label>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  )}
                  
                  <div className="flex items-center gap-3 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addBatchToProduct(productIndex)}
                      disabled={outOfStockProducts[productIndex]}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Add Another Batch
                    </Button>

                    {/* Use-By Date — inline next to Add Another Batch */}
                    <div className="flex items-center gap-2 ml-auto">
                      <div className="w-48">
                        <Label className={`text-xs font-medium ${!noUseDateProducts[productIndex] && !productUseDates[productIndex] ? 'text-amber-700 font-semibold' : 'text-muted-foreground'}`}>
                          Use-By Date {!noUseDateProducts[productIndex] && <span className="text-red-500">*</span>}
                          {useByPredictions[productIndex] && !noUseDateProducts[productIndex] && (
                            <span
                              className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-px rounded-full bg-violet-100 text-violet-700 text-[9px] font-semibold align-middle cursor-help"
                              title={predictionTitle(useByPredictions[productIndex])}
                              data-testid={`use-by-auto-badge-${productIndex}`}
                            >
                              <Sparkles className="w-2.5 h-2.5" />
                              auto {useByPredictions[productIndex].shelf_life_days}d
                            </span>
                          )}
                        </Label>
                        {noUseDateProducts[productIndex] ? (
                          <div className="h-9 flex items-center px-3 bg-amber-50 border border-amber-200 rounded-md">
                            <span className="text-xs font-medium text-amber-700">No Date</span>
                          </div>
                        ) : (
                          <DatePicker
                            value={productUseDates[productIndex] || ''}
                            onChange={(formattedDate) => setProductUseDates(prev => ({ ...prev, [productIndex]: formattedDate }))}
                            placeholder="Select date"
                            defaultMonth={useByPredictions[productIndex]?.predicted_date}
                            className={!productUseDates[productIndex] ? "border-amber-400 bg-amber-50/60 ring-1 ring-amber-300" : ""}
                            data-testid={`use-by-date-${productIndex}`}
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-4">
                        <Checkbox
                          id={`no-date-${productIndex}`}
                          checked={noUseDateProducts[productIndex] || false}
                          onCheckedChange={(checked) => {
                            setNoUseDateProducts(prev => ({ ...prev, [productIndex]: checked }));
                            if (checked) {
                              setProductUseDates(prev => ({ ...prev, [productIndex]: 'No Date' }));
                            } else {
                              setProductUseDates(prev => ({ ...prev, [productIndex]: '' }));
                            }
                          }}
                          className="h-3.5 w-3.5"
                          data-testid={`no-date-checkbox-${productIndex}`}
                        />
                        <label htmlFor={`no-date-${productIndex}`} className="text-[11px] text-muted-foreground cursor-pointer">
                          No Date
                        </label>
                      </div>
                      <div className="flex items-center gap-1.5 mt-2">
                        <Checkbox
                          id={`out-of-stock-${productIndex}`}
                          checked={outOfStockProducts[productIndex] || false}
                          onCheckedChange={(checked) => {
                            setOutOfStockProducts(prev => ({ ...prev, [productIndex]: checked }));
                          }}
                          className="h-3.5 w-3.5 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                          data-testid={`out-of-stock-checkbox-${productIndex}`}
                        />
                        <label htmlFor={`out-of-stock-${productIndex}`} className="text-[11px] text-red-600 font-semibold cursor-pointer">
                          Out Of Stock
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            <div className="flex justify-between gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <div className="flex gap-2">
                {onSplitFulfilment && (
                  <Button
                    variant="outline"
                    onClick={onSplitFulfilment}
                    className="border-primary/40 text-primary hover:bg-primary/5"
                    data-testid="btn-ready-split-fulfilment"
                    title="Not everything ready? Send what is ready now and keep the rest on a linked order under the same PO."
                  >
                    <Split className="w-4 h-4 mr-2" />
                    Split Fulfilment
                  </Button>
                )}
                {handleSaveDraft && (
                  <Button
                    variant="outline"
                    onClick={handleSaveDraft}
                    disabled={savingDraft}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="btn-save-batch-draft"
                    title="Save partial batch entry and resume later. Order status will NOT change."
                  >
                    {savingDraft ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save as Draft
                  </Button>
                )}
                <Button 
                  onClick={handleStep1Continue} 
                  disabled={!validateBatchCodes()}
                  data-testid="btn-step1-continue"
                >
                  Review & Create
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
            
            {/* Warning Confirmation Dialog */}
            <Dialog open={showWarningConfirm} onOpenChange={setShowWarningConfirm}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-amber-600">
                    <AlertTriangle className="w-5 h-5" />
                    Confirm Values
                  </DialogTitle>
                  <DialogDescription>
                    The following values differ from the original order. Please confirm to continue.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-4">
                  {batchValidationWarnings.map((warning, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm p-2 bg-amber-50 rounded border border-amber-200">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <span className="text-amber-800">{warning}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowWarningConfirm(false)}>
                    Go Back
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowWarningConfirm(false);
                      generateFinalBatchLines();
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Continue Anyway
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
        
        {/* Step 2: Review & Create (was Step 3) */}
        {batchStep === 3 && (
          <>
            <div className="space-y-4 py-2">
              {/* Professional Review Header */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900">Batch Creation Summary</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      Please review the batch details below. Upon confirmation, these batches will be created and the dispatch note will reflect all quantities and weights as displayed.
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary Totals */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold font-mono">{batchLines.filter(l => !l.out_of_stock).length}</p>
                  <p className="text-xs text-muted-foreground">Batch{batchLines.filter(l => !l.out_of_stock).length !== 1 ? 'es' : ''} to Create</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold font-mono">
                    {batchLines.filter(l => !l.out_of_stock).reduce((sum, l) => sum + (parseFloat(l.quantity) || 0), 0).toFixed(0)}
                  </p>
                  <p className="text-xs text-muted-foreground">Total Units</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center border border-blue-200">
                  <p className="text-2xl font-bold font-mono text-blue-700">
                    {batchLines.filter(l => !l.out_of_stock).reduce((sum, l) => sum + (parseFloat(l.weight_kg) || 0), 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-blue-600 font-medium">Total Weight (kg)</p>
                </div>
              </div>

              {/* Out of stock notice */}
              {batchLines.some(l => l.out_of_stock) && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                  <p className="text-sm text-red-700">
                    <span className="font-bold">{batchLines.filter(l => l.out_of_stock).length} product(s) marked Seasonally Out Of Stock</span> — excluded from totals, will appear on dispatch note.
                  </p>
                </div>
              )}

              {/* Batch Details Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 font-medium">Product</th>
                      <th className="text-left p-3 font-medium">Batch Code</th>
                      <th className="text-right p-3 font-medium">Units</th>
                      <th className="text-right p-3 font-medium">Weight (kg)</th>
                      <th className="text-right p-3 font-medium">Cases</th>
                      <th className="text-left p-3 font-medium">Use-By Date</th>
                      <th className="text-left p-3 font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {batchLines.map((line, index) => (
                      <tr key={index} className={line.out_of_stock ? 'bg-red-50' : 'hover:bg-muted/30'}>
                        <td className="p-3">
                          <span className="font-medium">{line.product_name}</span>
                          {line.out_of_stock && (
                            <span className="ml-2 px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded tracking-wider">SEASONALLY OUT OF STOCK</span>
                          )}
                        </td>
                        <td className="p-3 font-mono text-sm">{line.out_of_stock ? '—' : line.batch_code}</td>
                        <td className="p-3 text-right font-mono">{line.out_of_stock ? '—' : line.quantity}</td>
                        <td className="p-3 text-right font-mono text-blue-600 font-medium">{line.out_of_stock ? '—' : parseFloat(line.weight_kg || 0).toFixed(2)}</td>
                        <td className="p-3 text-right font-mono">{line.out_of_stock ? '—' : parseFloat(line.cases || 0).toFixed(1)}</td>
                        <td className={`p-3 font-mono text-sm ${line.expiry_date === 'No Date' ? 'text-amber-600 italic' : ''}`}>
                          {line.out_of_stock ? '—' : line.expiry_date === 'No Date' ? 'No Date' : line.expiry_date}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground truncate max-w-[150px]" title={line.notes || ''}>
                          {line.notes || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Confirmation Notice */}
              <div className={`${editingBatchData ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-amber-50 border-amber-200 text-amber-800'} border rounded-lg p-3`}>
                <p className="text-sm">
                  {editingBatchData ? (
                    <><strong>Edit mode:</strong> Lines that match an existing batch by ID will be reused. Order status (Ready / Dispatched) is preserved.</>
                  ) : (
                    <><strong>Note:</strong> Once created, batch codes cannot be changed. The dispatch note will be updated with these batch details and locked for dispatch.</>
                  )}
                </p>
              </div>

              {/* Actual pallet count — overrides the SO estimate, prints on dispatch note */}
              <div className="border rounded-lg p-3 bg-zinc-50/60" data-testid="ready-pallet-count-block">
                <Label htmlFor="ready-pallet-count" className="text-sm font-medium flex items-center gap-2">
                  Number of Pallets (Actual)
                  <span className="text-xs font-normal text-muted-foreground">
                    {pendingReadyDispatch?.num_pallets != null
                      ? `Estimate from Sales Order: ${pendingReadyDispatch.num_pallets}`
                      : 'No estimate from Sales Order'}
                  </span>
                </Label>
                <div className="flex items-center gap-3 mt-2">
                  <Input
                    id="ready-pallet-count"
                    type="number"
                    min="0"
                    step="1"
                    value={palletCount}
                    onChange={(e) => setPalletCount(e.target.value)}
                    placeholder="e.g. 4"
                    className="w-32 h-9"
                    data-testid="input-ready-pallet-count"
                  />
                  <p className="text-xs text-muted-foreground">
                    Updates <strong>dispatch.num_pallets</strong> and prints on the Dispatch Note. Leave blank to keep the SO estimate.
                  </p>
                </div>
              </div>
            </div>
            
            <DialogFooter className="flex justify-between sm:justify-between border-t pt-4">
              <Button variant="outline" onClick={() => setBatchStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Edit
              </Button>
              <div className="flex gap-2">
                {handleSaveDraft && (
                  <Button
                    variant="outline"
                    onClick={handleSaveDraft}
                    disabled={savingDraft || creatingBatch}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="btn-save-batch-draft-review"
                    title="Save partial batch entry and resume later. Order status will NOT change."
                  >
                    {savingDraft ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save as Draft
                  </Button>
                )}
                <Button
                  onClick={async () => {
                    await handleReadyWithBatch();
                    // Learn batch + weight formulas from entered data (only the missing parts are saved)
                    const lines = pendingReadyDispatch?.lines || [];
                    const customerId = pendingReadyDispatch?.customer_id;
                    if (customerId) {
                      for (let pi = 0; pi < lines.length; pi++) {
                        const line = lines[pi];
                        const batches = productBatches[pi] || [];
                        const f = batchFormulas[pi];
                        if (!line.product_id || (f?.batch_units_per_case && f?.unit_weight_kg)) continue;
                        for (const b of batches) {
                          const qty = parseFloat(b.quantity) || 0;
                          const cases = parseFloat(b.cases) || 0;
                          const weight = parseFloat(b.weight_kg) || 0;
                          if (qty > 0 && cases > 0) {
                            try {
                              await learnBatchFormula({
                                customer_id: customerId,
                                product_id: line.product_id,
                                cobranding_id: line.cobranding_id || null,
                                units: qty, cases: cases,
                                weight_kg: weight > 0 ? weight : null,
                              });
                            } catch { /* non-critical */ }
                            break; // Learn from first valid batch only
                          }
                        }
                      }
                    }
                  }}
                  disabled={creatingBatch}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-create-batch-ready"
                >
                  {creatingBatch ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                  {editingBatchData
                    ? `Update ${batchLines.length} Batch Line${batchLines.length !== 1 ? 's' : ''}`
                    : `Confirm & Create ${batchLines.length} Batch${batchLines.length !== 1 ? 'es' : ''}`}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
